import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Power } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { m: 15, l: '15 min' },
  { m: 30, l: '30 min' },
  { m: 60, l: '1 hour' },
  { m: 0, l: 'Custom' },
];

const MaintenanceSettings = () => {
  const adminId = (() => { try { return JSON.parse(localStorage.getItem('dws_user') || '{}').id; } catch { return null; } })();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("We're doing some quick maintenance. Please check back shortly.");
  const [audience, setAudience] = useState('handlers');
  const [duration, setDuration] = useState(30);
  const [customMin, setCustomMin] = useState(45);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/maintenance/status`);
      setStatus(res.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      const mins = duration === 0 ? Number(customMin) || 0 : duration;
      await axios.post(`${API}/admin/maintenance/set`, {
        admin_id: adminId, message, audience, duration_minutes: mins,
      });
      toast.success('Maintenance mode ON');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/maintenance/off`, { admin_id: adminId });
      toast.success('Maintenance mode OFF');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const isActive = status?.active;

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="maintenance-settings">
      <div className={`rounded-xl p-4 border ${isActive ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-1">
          <Wrench size={20} weight="fill" className={isActive ? 'text-amber-600' : 'text-gray-500'} />
          <h2 className="font-bold text-gray-800">Maintenance Mode</h2>
        </div>
        <p className="text-sm text-gray-500">
          {isActive
            ? `ON for ${status.audience === 'all' ? 'handlers + customers' : status.audience === 'customers' ? 'customers only' : 'handlers'}${status.ends_at ? ` · auto-off at ${new Date(status.ends_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })} IST` : ''}`
            : 'Currently OFF. Admins are never blocked.'}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Message shown to users</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg p-2 text-sm"
            data-testid="maintenance-message-input"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Who is blocked?</label>
          <div className="flex flex-wrap gap-2">
            {[{ k: 'handlers', l: 'Handlers only' }, { k: 'customers', l: 'Customers only' }, { k: 'all', l: 'Handlers + Customers' }].map((a) => (
              <button
                key={a.k}
                onClick={() => setAudience(a.k)}
                className={`px-3 py-2 rounded-lg text-sm font-semibold border ${audience === a.k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'}`}
                data-testid={`maintenance-audience-${a.k}`}
              >{a.l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Duration (auto-off)</label>
          <div className="flex gap-2 flex-wrap">
            {DURATIONS.map((d) => (
              <button
                key={d.m}
                onClick={() => setDuration(d.m)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${duration === d.m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'}`}
                data-testid={`maintenance-duration-${d.m}`}
              >{d.l}</button>
            ))}
          </div>
          {duration === 0 && (
            <input
              type="number" min="1" value={customMin}
              onChange={(e) => setCustomMin(e.target.value)}
              className="mt-2 w-32 border border-gray-300 rounded-lg p-2 text-sm"
              placeholder="Minutes"
              data-testid="maintenance-custom-min"
            />
          )}
          <p className="text-[11px] text-gray-400 mt-1">Custom = enter minutes. It also turns off automatically when time is up.</p>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={turnOn}
            disabled={busy}
            className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2"
            data-testid="maintenance-turn-on"
          ><Wrench size={18} weight="fill" /> Turn ON</button>
          <button
            onClick={turnOff}
            disabled={busy || !isActive}
            className="flex-1 bg-gray-700 hover:bg-gray-800 disabled:opacity-40 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2"
            data-testid="maintenance-turn-off"
          ><Power size={18} weight="fill" /> Turn OFF</button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSettings;
